import React, { useCallback, useEffect, useState } from "react";
import { Box, Tabs, Tab, Chip, Tooltip } from "@mui/material";
import ListPageLayout from "../../components/ListPageLayout";
import { fetchConsentExtendedList, fetchStudies } from "../../utils/api";
import { formatDate } from "../../utils/formatDateTime";
import { BASE_CHIP_SX, getStatusChipStyle } from "../../constants/statusColors";
import "styles/Dashboard.css";

// Grid column -> backend sort key. The backend sorts real `consent` columns, so
// the de-identified ID column orders by patientId (1:1 with the fingerprint)
// and the study chip orders by irbStudy.
const SORT_FIELD_BY_COLUMN = {
  fingerprint: "patientId",
  studyLabel: "irbStudy",
  consentDate: "consentDate",
};

const DEFAULT_SORT_MODEL = [{ field: "consentDate", sort: "desc" }];

const truncateFingerprint = (fp) => {
  if (!fp) return "—";
  return fp;
};

const ConsentManagementDashboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [tabValue, setTabValue] = useState("STUDY_CONSENT");

  const studiesMapRef = React.useRef(null);

  const sortEntry = sortModel && sortModel.length > 0 ? sortModel[0] : null;
  const sortBy = sortEntry ? SORT_FIELD_BY_COLUMN[sortEntry.field] : undefined;
  const sortDirection = sortEntry && sortEntry.sort ? sortEntry.sort.toUpperCase() : undefined;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      if (!studiesMapRef.current) {
        const studies = await fetchStudies().catch(() => []);
        const map = {};
        (studies || []).forEach(s => {
          map[s.irbStudy] = s.studyShortName || s.irbStudy;
        });
        studiesMapRef.current = map;
      }

      const response = await fetchConsentExtendedList({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        consentType: tabValue,
        sortBy,
        sortDirection,
      });
      const list =
        response?.content ||
        response?.consents ||
        response?.data ||
        (Array.isArray(response) ? response : []);

      const total =
        response?.totalElements ??
        response?.total ??
        (Array.isArray(list) ? list.length : 0);

      const transformed = (Array.isArray(list) ? list : []).map((item, index) => ({
        id: item.consentId || index,
        fingerprint: item.fingerprint || "—",
        studyLabel: studiesMapRef.current[item.irbStudy] || item.irbStudy || "—",
        verifiedPdf: item.verifiedPDF ? "Yes" : "No",
        consentDate: item.consentDate ? formatDate(item.consentDate) : "—",
      }));

      setRows(transformed);
      setTotalRowCount(total);
    } catch (error) {
      console.error("Error fetching de-identified consent list:", error);
      setRows([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, tabValue, sortBy, sortDirection]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // The list only returns currently active consents, so a "Consent Status"
  // column would read "Active" on every row and has been removed.
  const columns = [
    {
      field: "fingerprint",
      headerName: "De-identified ID",
      minWidth: 220,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span style={{ fontFamily: "monospace" }}>
            {truncateFingerprint(params.value)}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "studyLabel",
      headerName: "Study",
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            ...BASE_CHIP_SX,
            backgroundColor: "#ffffff",
            color: "#500000",
            border: "1px solid #500000",
          }}
        />
      ),
    },
    {
      field: "verifiedPdf",
      headerName: "Verified PDF",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ ...BASE_CHIP_SX, ...getStatusChipStyle(params.value) }}
        />
      ),
    },
    { field: "consentDate", headerName: "Consent Date", width: 130, sortable: true },
  ].filter(c => !(tabValue === "GENERAL_CONSENT" && c.field === "studyLabel"));

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (newSortModel) => {
    const supported = (newSortModel || []).filter(
      (entry) => SORT_FIELD_BY_COLUMN[entry.field]
    );
    setSortModel(supported.length > 0 ? supported : DEFAULT_SORT_MODEL);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <ListPageLayout
      title="Consent Management"
      headerActions={
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Study Consent" value="STUDY_CONSENT" />
            <Tab label="General Consent" value="GENERAL_CONSENT" />
          </Tabs>
        </Box>
      }
      gridProps={{
        rows,
        columns,
        pageSizeOptions: [10, 25, 50],
        pagination: true,
        paginationMode: "server",
        rowCount: totalRowCount,
        paginationModel,
        onPaginationModelChange: setPaginationModel,
        sortingMode: "server",
        sortModel,
        onSortModelChange: handleSortModelChange,
        loading,
      }}
    />
  );
};

export default ConsentManagementDashboard;
